import { ButtonRow, Form } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { fetchJson } from '@frontend/store/fetch';
import type { IReduxState } from '../../../../../configureStore';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getSelectedLanguage } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { diagnosticReportAddEditExaminationShowToast } from '../../utils/v1/diagnosticReportAddEditExaminationShowToast';
import { TcohV2DiagnosticReportAddEditCustomControlFields } from './TcohV2DiagnosticReportAddEditCustomControlFields';

export function TcohV2DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const translate = getSelectedLanguage();
    const episodeContext = useEpisodeContext();
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [isAdviesBijAfwijkendeBeoordelingRequested, setIsAdviesBijAfwijkendeBeoordelingRequested] = useState(false);
    const [adviesBijAfwijkendeBeoordelingVraag, setAdviesBijAfwijkendeBeoordelingVraag] = useState<string>();

    useEffect(() => {
        loadAdviesBijAfwijkendeBeoordelingRequest();
    }, []);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                isFirstItem={props.isFirstItem}
                isItemOpen
                practitionerName={
                    props.currentDiagnosticReport && props.currentDiagnosticReport.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(
                    props.service.code,
                    'diagnosticReport',
                    translate.diagnosticReport.category_examinationReport
                )}
            />

            <FormContextProvider
                value={{
                    customControlData: true,
                    errors: props.errors,
                    item: props.diagnosticReport.customControlData,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    placeholderGroupName: 'diagnosticReport',
                }}
            >
                <Form submitOnEnter={false}>
                    <TcohV2DiagnosticReportAddEditCustomControlFields
                        adviesBijAfwijkendeBeoordelingVraag={adviesBijAfwijkendeBeoordelingVraag}
                        customControlData={props.diagnosticReport.customControlData}
                        errors={props.errors}
                        handleChangeCustomControl={props.handleChangeCustomControl}
                        isAdviesBijAfwijkendeBeoordelingRequested={isAdviesBijAfwijkendeBeoordelingRequested}
                        service={props.service}
                        validateOneCustomControl={props.validateOneCustomControl}
                    />

                    <ButtonRow justify="right">
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            isExisting={Boolean(props.request.id)}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveDiagnosticReport('draft');
                            }}
                        />
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveDiagnosticReport('final');
                            }}
                        />
                    </ButtonRow>
                </Form>
            </FormContextProvider>
        </ProcessContainer>
    );

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }
        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);

        await diagnosticReportAddEditExaminationShowToast(
            saveType,
            props.service,
            selectedEmploymentId,
            episodeContext.patient
        );

        setButtonDisabled(false);
        await props.onSave(diagnosticReportId);
    }

    async function loadAdviesBijAfwijkendeBeoordelingRequest() {
        let customControlData;

        if (!props.request.isDirectProcedureAdded) {
            customControlData = props.request.customControlData;
        } else if (props.process?.procedure?.id) {
            const procedureResult = await fetchJson(
                `/api/procedures/${props.process.procedure.id}/customControlData`,
                'GET'
            );
            customControlData = procedureResult.json.customControlData;
        } else {
            customControlData = {};
        }

        setIsAdviesBijAfwijkendeBeoordelingRequested(customControlData.adviesBijAfwijkendeBeoordeling === 'Ja');
        setAdviesBijAfwijkendeBeoordelingVraag(customControlData.adviesBijAfwijkendeBeoordelingVraag);
    }
}
